import type { OrgEventChecklistNotifyTemplate } from 'hooks/orgEvents/orgEventHooks';
import {
  NotificationPriority,
  NotificationType,
  type CreateNotificationFormFields,
} from 'hooks/useNotification';

/** Fields a checklist template can fill on the Create Notification composer. */
export type ChecklistNotifyComposeSeed = Pick<
  CreateNotificationFormFields,
  | 'priority'
  | 'emailSubject'
  | 'emailMessage'
  | 'smsMessage'
  | 'teamsMessage'
  | 'phoneMessage'
  | 'appMessage'
  | 'userIds'
  | 'teamIds'
  | 'notificationTypes'
>;

const PRIORITIES = new Set<string>(Object.values(NotificationPriority));

/** Channels the composer can actually select. Teams stays locked. */
const COMPOSER_CHANNELS = new Set<string>([
  NotificationType.IN_APP,
  NotificationType.EMAIL,
  NotificationType.SMS,
]);

export function interpolateNotifyTemplate(
  template: string | null | undefined,
  eventName: string
): string {
  if (!template) return '';
  return template
    .replaceAll('{eventName}', eventName)
    .replaceAll('{stormName}', eventName)
    .replaceAll('{featureName}', eventName)
    .replaceAll('{resourceCount}', '')
    .replaceAll('{thresholdDescription}', '')
    .replaceAll('{layerLabel}', '');
}

/**
 * Partial Create Notification values from a response-plan notification
 * template. Confirm receipt, roles, area, incident, and schedule stay at
 * composer defaults.
 */
export function notificationComposeSeedFromChecklist(input: {
  label?: string | null;
  notify?: OrgEventChecklistNotifyTemplate | null;
  eventName: string;
}): ChecklistNotifyComposeSeed {
  const notify = input.notify;
  const eventName = input.eventName.trim() || 'Event';
  const title = interpolateNotifyTemplate(
    notify?.titleTemplate || input.label,
    eventName
  );
  const body = interpolateNotifyTemplate(
    notify?.bodyTemplate || input.label,
    eventName
  );
  const channels = (notify?.notificationTypes ?? []).filter(
    (type): type is NotificationType => COMPOSER_CHANNELS.has(type)
  );
  const priority =
    notify?.priority && PRIORITIES.has(notify.priority)
      ? (notify.priority as NotificationPriority)
      : NotificationPriority.INFO;

  return {
    priority,
    emailSubject:
      interpolateNotifyTemplate(notify?.emailSubject, eventName) || title,
    emailMessage:
      interpolateNotifyTemplate(notify?.emailMessage, eventName) || body,
    smsMessage:
      interpolateNotifyTemplate(notify?.smsMessage, eventName) || body,
    teamsMessage:
      interpolateNotifyTemplate(notify?.teamsMessage, eventName) || body,
    phoneMessage:
      interpolateNotifyTemplate(notify?.phoneMessage, eventName) || body,
    appMessage:
      interpolateNotifyTemplate(notify?.appMessage, eventName) || body,
    userIds: notify?.userIds ?? [],
    teamIds: notify?.teamIds ?? [],
    notificationTypes: channels.length ? channels : [NotificationType.IN_APP],
  };
}
